import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, HelpCircle } from 'lucide-react';
import { Question } from '../../../types';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { VisualAidFactory } from '../../visuals/VisualAidFactory';
import { StatusBadge } from './StatusBadge';
import { AnswerComparison } from './AnswerComparison';
import { ExplanationCard } from './ExplanationCard';
interface FeedbackDisplayProps {
  question: Question;
  wasCorrect: boolean;
  userAnswer: string;
  userAnswerIndex?: number; // NIEUW: de index van het gekozen antwoord
  motivationalText: string;
  xpGained: number;
  onNext: () => void;
  isLastQuestion: boolean;
  mode?: 'full' | 'compact';
}
/**
 * Unified feedback display component
 * Used in both PracticeSession (immediate feedback) and CitoSimulator (review)
 */
export function FeedbackDisplay({
  question,
  wasCorrect,
  userAnswer,
  userAnswerIndex,
  motivationalText,
  xpGained,
  onNext,
  isLastQuestion,
  mode = 'full'
}: FeedbackDisplayProps) {
  const correctAnswer = question.options[question.correctAnswer];
  // CRITICAL FIX: Get distractor explanation using INDEX, not answer text
  // All distractor explanations use index-based keys: '0', '1', '2', '3'
  const distractorExplanation = !wasCorrect && userAnswerIndex !== undefined ? question.distractorExplanations?.[userAnswerIndex.toString()] || 'Helaas, dat was niet het juiste antwoord. Kijk goed naar de uitleg hieronder om te zien hoe het wel moet!' : undefined;
  return <div className="flex-1 flex flex-col" data-id="element-621">
      {/* LARGE STATUS BADGE */}
      <StatusBadge isCorrect={wasCorrect} motivationalText={motivationalText} xpGained={xpGained} mode={mode} data-id="element-622" />

      <div className="space-y-6 mb-8" data-id="element-623">
        {/* ANSWER COMPARISON */}
        {!wasCorrect && <AnswerComparison userAnswer={userAnswer} correctAnswer={correctAnswer} isCorrect={wasCorrect} mode={mode} data-id="element-624" />}

        {/* EXPLANATIONS */}
        <ExplanationCard explanation={question.explanation || ''} distractorExplanation={distractorExplanation} isCorrect={wasCorrect} mode={mode} data-id="element-625" />

        {/* VISUAL AID */}
        {question.visualAid && <>
            {(!question.visualAid.showWhen || question.visualAid.showWhen === 'always' || question.visualAid.showWhen === 'wrong' && !wasCorrect || question.visualAid.showWhen === 'correct' && wasCorrect) && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} data-id="element-626">
                <Card className="p-6 border-2 border-indigo-100 shadow-md" data-id="element-627">
                  <div className="flex items-center gap-2 mb-4 text-indigo-900 font-bold" data-id="element-628">
                    <HelpCircle className="w-6 h-6 text-indigo-500" data-id="element-629" />
                    <span data-id="element-630">Kijk maar eens:</span>
                  </div>
                  <VisualAidFactory visualAid={question.visualAid} isCorrect={wasCorrect} data-id="element-631" />
                </Card>
              </motion.div>}
          </>}
      </div>

      {/* ACTION BUTTON */}
      {mode === 'full' && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.5
    }} className="mt-auto" data-id="element-632">
          <Button fullWidth size="xl" onClick={onNext} variant={wasCorrect ? 'success' : 'primary'} className="h-16 text-xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1" data-id="element-633">
            {wasCorrect ? 'Volgende vraag' : 'Ik snap het nu! 💡'}
            <ArrowRight className="ml-3 w-6 h-6" data-id="element-634" />
          </Button>
        </motion.div>}
    </div>;
}